import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { testIds } from "@/testIds";
import {
  COLUMNS,
  FONT_SIZES,
  HEADING_NUMBERS,
  INDENT,
  LANGUAGES,
  LINE_SPACING,
  MARGINS,
  PAGE_NUMBERS,
  PAPER,
  labelOf,
  languageLabel,
  sayChoice,
  marginPatch,
  marginPreset,
  revertPatch,
  type Choice,
  type DocSettingsPatch,
  type DocSettingsValues,
} from "@/lib/mobileSettings";
import { PublishWith } from "@/components/editor/DocumentStamp/sections/PublishWith";
import styles from "./MobileReader.module.scss";

const M = testIds.mobile;
const UNDO_MS = 5000;

export interface MobileSettingsSheetProps {
  values: DocSettingsValues;
  /** Owners and editors change settings; anyone else reads them. */
  canEdit: boolean;
  /** "Article", "Report"…: the type, shown read-only. */
  typeLabel?: string;
  onChange: (patch: DocSettingsPatch) => Promise<void>;
  onRename: (title: string) => Promise<void>;
  /**
   * Publisher templates on the phone (25 Oct 2026): the venue, as "Publish with…" on desktop. The
   * venue sets the page, so page and text settings are locked while one is chosen. Only the owner
   * changes it. Without onPublisher, the group isn't shown.
   */
  publisherProfile?: string | null;
  isOwner?: boolean;
  onPublisher?(profileId: string): Promise<void>;
}

/**
 * Page & settings on a phone (Olivia, 10 Oct). Every setting is a tap: segmented
 * choices and a language list without search. The one text field is the title,
 * for owners and editors. Each change saves at once with a 5-second Undo. What
 * needs typing (custom margins, header and footer, the class and its options)
 * shows read-only, "Change on desktop".
 */
export function MobileSettingsSheet({ values, canEdit: mayEdit, typeLabel, onChange, onRename, publisherProfile, isOwner = true, onPublisher }: MobileSettingsSheetProps) {
  // A venue sets the page and the text layout (its class does): those lock here, as on desktop. The
  // title and the language stay the author's.
  const canEdit = mayEdit && !publisherProfile;
  const { t } = useTranslation("mobile");
  const said = (what: string, value: string | number) => t("settings.changed", { what, value });
  const [undo, setUndo] = useState<{ label: string; patch: DocSettingsPatch } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const apply = async (label: string, patch: DocSettingsPatch) => {
    const back = revertPatch(values, patch);
    setError(null);
    try {
      await onChange(patch);
      setUndo({ label, patch: back });
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
    } catch {
      setError(t("settings.saveFailed"));
    }
  };
  const undoLast = async () => {
    if (!undo) return;
    const patch = undo.patch;
    setUndo(null);
    try {
      await onChange(patch);
    } catch {
      setError(t("settings.undoFailed"));
    }
  };

  const margins = marginPreset(values);

  return (
    <div className={styles.settingsBody} data-testid={M.settingsSheet}>
      <Row label={t("settings.title")}>
        {mayEdit ? <TitleField title={values.title} onRename={onRename} /> : <span className={styles.settingValue}>{values.title || t("docs.untitled")}</span>}
      </Row>

      {onPublisher && (
        <>
          <h4 className={styles.settingsGroup}>{t("settings.publisher")}</h4>
          <div data-testid={M.settingsPublisher}>
            <PublishWith
              value={publisherProfile}
              disabled={!mayEdit}
              ownerOnly={mayEdit && !isOwner}
              onChange={(id) => void onPublisher(id).catch(() => setError(t("settings.saveFailed")))}
            />
          </div>
          {publisherProfile && <p className={styles.settingNote} data-testid={M.settingsVenueLock}>{t("settings.setByVenue")}</p>}
        </>
      )}

      <h4 className={styles.settingsGroup}>{t("settings.page")}</h4>
      <Choices setting="paper" label={t("settings.paper")} choices={PAPER} value={values.paperSize ?? "a4"} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.paper"), labelOf(PAPER, v)), { paperSize: v })} />
      <Choices setting="columns" label={t("settings.columns")} choices={COLUMNS} value={values.columns ?? 1} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.columns"), v), { columns: v })} />
      <Choices setting="fontSize" label={t("settings.fontSize")} choices={FONT_SIZES} value={values.fontSize ?? 11} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.fontSize"), `${v} pt`), { fontSize: v })} />
      <Choices setting="margins" label={t("settings.margins")} choices={MARGINS} value={margins === "custom" ? null : margins} canEdit={canEdit}
        note={margins === "custom" ? t("settings.marginsCustom") : undefined}
        onPick={(v) => apply(said(t("settings.margins"), labelOf(MARGINS, v)), marginPatch(v))} />

      <h4 className={styles.settingsGroup}>{t("settings.text")}</h4>
      <Choices setting="lineSpacing" label={t("settings.lineSpacing")} choices={LINE_SPACING} value={String(values.lineSpacing ?? 1)} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.lineSpacing"), v), { lineSpacing: Number(v) })} />
      <Choices setting="paragraphIndent" label={t("settings.paragraphIndent")} choices={INDENT} value={values.paragraphIndent || null} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.paragraphIndent"), labelOf(INDENT, v)), { paragraphIndent: v })} />
      <Choices setting="pageNumbering" label={t("settings.pageNumbers")} choices={PAGE_NUMBERS} value={values.pageNumbering || "arabic"} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.pageNumbers"), labelOf(PAGE_NUMBERS, v)), { pageNumbering: v })} />
      <Choices setting="headingNumbering" label={t("settings.headingNumbers")} choices={HEADING_NUMBERS}
        value={values.headingNumbering == null || values.headingNumbering < 0 ? -1 : values.headingNumbering} canEdit={canEdit}
        onPick={(v) => apply(said(t("settings.headingNumbers"), labelOf(HEADING_NUMBERS, v)), { headingNumbering: v })} />

      <Row label={t("settings.language")}>
        {mayEdit ? (
          <div className={styles.languageList} role="radiogroup" aria-label={t("settings.language")}>
            {LANGUAGES.map((l) => {
              const on = (values.language || "auto") === l.value;
              return (
                <button key={l.value} type="button" role="radio" aria-checked={on} data-testid={M.settingsLanguage} data-value={l.value}
                  className={styles.languageRow} onClick={() => !on && void apply(said(t("settings.language"), languageLabel(l.value, t("settings.languageAuto"))), { language: l.value })}>
                  <span>{languageLabel(l.value, t("settings.languageAuto"), l.value === "auto" ? values.detectedLanguage : null)}</span>
                  {on && <span aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>
        ) : (
          <span className={styles.settingValue}>{languageLabel(values.language, t("settings.languageAuto"), values.detectedLanguage)}</span>
        )}
      </Row>

      <h4 className={styles.settingsGroup}>{t("settings.onDesktop")}</h4>
      {[
        [t("settings.typeClass"), [typeLabel, values.documentClass].filter(Boolean).join(" · ") || t("settings.default")],
        [t("settings.classOptions"), values.classOptions || t("settings.none")],
        [t("settings.header"), values.headerText || t("settings.none")],
        [t("settings.footer"), values.footerText || t("settings.none")],
      ].map(([label, value]) => (
        <div key={label} className={styles.desktopRow} data-testid={M.settingsDesktopRow}>
          <span className={styles.settingLabel}>{label}</span>
          <span className={styles.settingValue}>{value}</span>
          {mayEdit && <span className={styles.muted}>{t("settings.changeOnDesktop")}</span>}
        </div>
      ))}

      {error && <p className={styles.shareError} role="alert">{error}</p>}
      {undo && (
        <div className={styles.undoToast} role="status" data-testid={M.settingsUndo}>
          <span>{undo.label}</span>
          <button type="button" data-testid="mobile-settings-undo-button" onClick={() => void undoLast()}>
            {t("settings.undo")}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.settingRow}>
      <span className={styles.settingLabel}>{label}</span>
      {children}
    </div>
  );
}

/** A segmented choice; a viewer gets the value as text. */
function Choices<T extends string | number>({
  setting,
  label,
  choices,
  value,
  canEdit,
  note,
  onPick,
}: {
  setting: string;
  label: string;
  choices: Choice<T>[];
  value: T | null;
  canEdit: boolean;
  note?: string;
  onPick: (v: T) => void;
}) {
  const { t } = useTranslation("mobile");
  return (
    <Row label={label}>
      {canEdit ? (
        <div className={styles.segmented} role="radiogroup" aria-label={label}>
          {choices.map((c) => {
            const on = value !== null && String(c.value) === String(value);
            return (
              <button key={String(c.value)} type="button" role="radio" aria-checked={on} data-testid={M.settingsOption}
                data-setting={setting} data-value={String(c.value)} onClick={() => !on && onPick(c.value)}>
                {sayChoice(c.label)}
              </button>
            );
          })}
        </div>
      ) : (
        <span className={styles.settingValue}>{value === null ? t("settings.custom") : labelOf(choices, value)}</span>
      )}
      {note && <p className={styles.muted}>{note}</p>}
    </Row>
  );
}

/** The title: the one text field on a phone. Saves on Enter or when it loses focus. */
function TitleField({ title, onRename }: { title: string; onRename: (t: string) => Promise<void> }) {
  const { t } = useTranslation("mobile");
  const [draft, setDraft] = useState(title);
  useEffect(() => setDraft(title), [title]);
  const commit = () => {
    const next = draft.trim();
    if (next && next !== title) void onRename(next);
    else setDraft(title);
  };
  return (
    <input
      className={styles.titleInput}
      data-testid={M.settingsTitle}
      value={draft}
      aria-label={t("settings.titleField")}
      enterKeyHint="done"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") {
          setDraft(title);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}
